/// <reference path="../../setup/_references.ts" />

module onvio.practice.widgets {
    'use strict';

    /**
     * Summary items include the ability to load further details
     */
    interface ITimeSummary extends cs.INamedReferenceDto {
        /** Load the details */
        loadDetails: (filter: any) => ng.IPromise<any>;
        /** Details */
        details?: cs.ITimeAndExpenseV3Dto[];
        /** Translation key for the time column (hours, minutes, units) */
        timeColumnHeaderKey?: string;
    }

    enum TimeEntryStyle {
        hours = 'HOURS',
        minutes = 'MINUTES',
        units = 'TIMESPANUNITS'
    }

    export class BmPracticeWidgetTimeSummary implements IBmPracticeWidgetService {

        /** Flag if the widget is busy */
        public isBusy: boolean = false;

        /** Ensure the invoice widgets refresh together */
        public coalescingServiceArea: string = 'time';

        /** User setting to use units or minutes instead of hours */
        public timeEntryStyle = TimeEntryStyle.hours;
        public minutesPerUnit = 1;

        /** Templates for the widget */
        public bodyTemplate: string = 'practice-widgets/time-summary/time-summary.html';
        public headerTemplate: string = 'practice-widgets/practice-widget-date-search-header.html';
        public searchBox: boolean = false;

        public summaries: ITimeSummary[] = [{
            id: 'billable',
            // /* i18nextract */'Bluemoon.TimeEntry.TimeRecap.Summary.BillableLabel'
            name: 'Bluemoon.TimeEntry.TimeRecap.Summary.BillableLabel',
            loadDetails: (filter: any) => this.dataService.loadBillableTimeEntries(filter)
        }, {
            id: 'nonbillable',
            // /* i18nextract */'Bluemoon.TimeEntry.TimeRecap.Summary.NonbillableLabel'
            name: 'Bluemoon.TimeEntry.TimeRecap.Summary.NonbillableLabel',
            loadDetails: (filter: any) => this.dataService.loadNonbillableTimeEntries(filter)
        }, {
            id: 'administrative',
            // /* i18nextract */'Bluemoon.TimeEntry.TimeRecap.Summary.AdministrativeLabel'
            name: 'Bluemoon.TimeEntry.TimeRecap.Summary.AdministrativeLabel',
            loadDetails: (filter: any) => this.dataService.loadAdministrativeTimeEntries(filter)
        }, {
            id: 'total',
            // /* i18nextract */'Bluemoon.TimeEntry.TimeRecap.Summary.TotalLabel'
            name: 'Bluemoon.TimeEntry.TimeRecap.Summary.TotalLabel',
            loadDetails: (filter: any) => this.dataService.loadAllTimeEntries(filter)
        }];

        static $inject = [
            '$q',
            '$uibModal',
            'bm.practice.timeAndExpense.TimeRecapWidgetService',
            'bmpBigNumberUtils',
            'bm.practice.timeAndExpense.userPreferences.CurrentUserPreferencesTimeAndExpenseEntryService'
        ];
        constructor(
            private $q: ng.IQService,
            private $uibModal: angular.ui.bootstrap.IModalService,
            private dataService: any, /* bm.practice.timeAndExpense.TimeRecapWidgetService, */
            private bigNumber: bmp.globalize.IBigNumberUtils,
            private currentUserPreferencesService: bm.practice.timeAndExpense.userPreferences.ICurrentUserPreferencesTimeAndExpenseEntryService) {
            // Nothing required here
        }

        public initialize(widget: IBmPracticeWidgetController, done: () => void): void {
            // Remove the 'ALL' option as it's not appropriate and default to 'MONTH'
            widget.dateRangeSelectorOptions.shift();
            widget.loadDataArgs = { dateRangeId: 'MONTH' };
            done();
        }

        public loadData(args: IBmPracticeWidgetLoadDataArgs): ng.IPromise<any> {

            if (!args.dateRangeId || !args.startDate || !args.endDate) {
                return this.$q.when(undefined);
            }

            // Refresh the preferences each time in case they have been changed
            this.currentUserPreferencesService.get().then(preferences => {
                this.timeEntryStyle = preferences.timeEntryStyle.id as TimeEntryStyle;
                this.minutesPerUnit = preferences.timeEntryMinutesPerTimeSpanUnit;
            });

            return this.dataService.loadSummaries({
                dateRangeId: args.dateRangeId,
                startDate: args.startDate,
                endDate: args.endDate
            }).then((response: any & { [index: string]: any; }) => { // TODO - ng161 - handle reject from get
                // Generate a formatted version of each value
                _.forEach(this.summaries, (summary: ITimeSummary) => {
                    response[summary.id + 'Formatted'] = this.bigNumber.formatDecimal(this.bigNumber.parseDecimal(response[summary.id] || '0'), 'n2');
                    // Delete any cached details from a previous filter
                    delete summary.details;
                });
                return response;
            });

        }

        /** Opens the details of a summary area in a modal */
        public openDetails(summary: ITimeSummary, filter: any): void {

            // Create a controller for the modal
            let reportDetailsModalController: any = function($uibModalInstance: any, summary: ITimeSummary) {
                this.summary = summary;
                this.close = () => {
                    $uibModalInstance.dismiss('close');
                }
            };
            reportDetailsModalController.$inject = ['$uibModalInstance', 'summary'];

            let calculateTimeColumnValue = ((timeEntryStyle: TimeEntryStyle, minutesPerUnit: number = 1): (time: number) => string => {
                switch (timeEntryStyle) {
                    case TimeEntryStyle.units: return (time: number) => parseFloat((time / (60 * minutesPerUnit)).toString()).toFixed(2);
                    case TimeEntryStyle.minutes: return (time: number) => parseFloat((time / 60).toString()).toFixed(2);
                    default: case TimeEntryStyle.hours: return (time: number) => parseFloat((time / 3600).toString()).toFixed(2);
                }
            })(this.timeEntryStyle, this.minutesPerUnit);

            this.$uibModal.open({
                templateUrl: 'timesummary-modal.html',
                size: 'lg',
                controller: reportDetailsModalController,
                controllerAs: 'modal',
                resolve: {
                    summary: () => {
                        // Only load the details if there's no cached details available
                        if (!summary.details) {
                            summary.loadDetails(filter)
                                .then(details => {
                                    summary.details = [];
                                    _.forEach(details.items, (item: cs.ITimeAndExpenseV3Dto & { $date?: string, $hours?: string }) => {
                                        item.$date = moment(item.entryDate).format('L');
                                        item.$hours = calculateTimeColumnValue(item.timeSeconds);
                                        summary.details.push(item);
                                    });
                                });
                        }
                        summary.timeColumnHeaderKey = this.currentUserPreferencesService.getHoursLabelKey(this.timeEntryStyle);
                        return summary;
                    }
                },
            });

        }

    }


    angular.module('bm.practice.widgets.timeSummary', ['bm.practice.timeAndExpense.TimeRecapWidgetService'])
        .service('bm-practice-widget-time-summary', BmPracticeWidgetTimeSummary);
}
